// modules/ad-assets/PagesTab.tsx
// Таблиця Fan Pages з фільтром по акаунту

import React, { useEffect, useState } from 'react';
import { useAssets } from './useAssets.ts';
import { PageDetailsModal } from './PageDetailsModal.tsx';
import { PublishPostModal } from './PublishPostModal.tsx';
import type { Page, PageGroup } from '../../core/types.ts';

interface Props {
  socialAccountId: string;
  accountName?: string | null;
}

function formatFanCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function PagesTab({ socialAccountId, accountName }: Props) {
  const { 
    pages, pagesLoading, pagesError, fetchPages, refreshPages, pagesRefreshing,
    pageDetails, pageDetailsLoading, fetchPageDetails, updatePageDetails, syncPageDetails,
    groups, groupsLoading, fetchGroups, createGroup, deleteGroup, assignGroup,
    syncPagePosts, syncingPagePosts,
  } = useAssets();
  
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [publishingPageId, setPublishingPageId] = useState<string | null>(null);
  const [postSyncStatus, setPostSyncStatus] = useState<Record<string, 'ok' | 'error'>>({});
  
  const [isManageGroupsOpen, setIsManageGroupsOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (socialAccountId) {
      fetchPages(socialAccountId);
    }
  }, [socialAccountId, fetchPages]);

  return (
    <div className="page-body" style={{ padding: 0 }}>
      <div style={{ marginBottom: 'var(--space-5)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-1)' }}>
            Fan Pages
            {pages.length > 0 && (
              <span className="text-subtle font-mono text-sm" style={{ marginLeft: 'var(--space-2)' }}>
                ({pages.length})
              </span>
            )}
          </h2>
          {accountName && (
            <p className="text-muted text-sm" style={{ margin: 0 }}>Акаунт: <span className="text-subtle">{accountName}</span></p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            className="btn btn--secondary btn--sm"
            onClick={() => refreshPages(socialAccountId)}
            disabled={pagesRefreshing || pagesLoading}
            title="Оновити тільки сторінки з Facebook"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
          >
            <span
              style={{
                display: 'inline-block',
                fontSize: '0.9rem',
                animation: pagesRefreshing ? 'spin 1s linear infinite' : 'none',
              }}
            >
              ↻
            </span>
            {pagesRefreshing ? 'Оновлення…' : 'Оновити сторінки'}
          </button>
          <button 
            className="btn btn--outline" 
            onClick={() => setIsManageGroupsOpen(true)}
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
          >
            Управління групами
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {pagesLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 64, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Error */}
      {pagesError && !pagesLoading && (
        <div className="notification notification--error">{pagesError}</div>
      )}

      {/* Empty state */}
      {!pagesLoading && !pagesError && pages.length === 0 && (
        <div className="glass-card empty-state">
          <div className="empty-state__title">Немає сторінок</div>
          <div className="empty-state__desc">
            Синхронізуй акаунт для завантаження підключених Fan Pages.
          </div>
        </div>
      )}

      {/* Pages List */}
      {!pagesLoading && pages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {pages.map((page: Page) => (
            <div 
              key={page.id} 
              className="glass-card hover:bg-slate-800/50 transition-colors" 
              style={{ padding: 'var(--space-3) var(--space-4)', display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: 'var(--color-surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {page.pictureUrl ? (
                  <img
                    src={page.pictureUrl}
                    alt={page.name ?? ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '1rem', color: 'var(--color-text-subtle)' }}>P</span>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setSelectedPageId(page.id)}>
                <div
                  className="font-medium"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: '2px',
                  }}
                  title={page.name ?? page.id}
                >
                  {page.name ?? page.id}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="font-mono text-xs text-subtle">{page.id}</span>
                  <span className="text-xs text-subtle">
                    {formatFanCount(page.fanCount)} підписників
                  </span>
                  {page.category && (
                    <span className="badge badge--currency" style={{ fontSize: '0.65rem' }}>
                      {page.category}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexShrink: 0 }}>
                <select 
                  className="input" 
                  style={{ width: '120px', padding: '0.25rem 0.5rem', fontSize: '0.85rem', height: '30px' }}
                  value={page.groupId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    assignGroup(page.id, val);
                  }}
                >
                  <option value="">Без групи</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>

                {/* Sync Posts button */}
                <button
                  className={`btn ${
                    postSyncStatus[page.id] === 'ok'
                      ? 'btn--success'
                      : postSyncStatus[page.id] === 'error'
                      ? 'btn--danger'
                      : 'btn--outline'
                  }`}
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', height: '30px', whiteSpace: 'nowrap' }}
                  disabled={syncingPagePosts[page.id]}
                  title="Завантажити останні пости цієї сторінки"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const res = await syncPagePosts(page.id);
                    setPostSyncStatus((prev) => ({
                      ...prev,
                      [page.id]: res.ok ? 'ok' : 'error',
                    }));
                    setTimeout(() => {
                      setPostSyncStatus((prev) => {
                        const next = { ...prev };
                        delete next[page.id];
                        return next;
                      });
                    }, 3000);
                  }}
                >
                  {syncingPagePosts[page.id]
                    ? '⟳'
                    : postSyncStatus[page.id] === 'ok'
                    ? '✓ Пости'
                    : postSyncStatus[page.id] === 'error'
                    ? '✗ Помилка'
                    : '↻ Пости'}
                </button>

                <button 
                  className="btn btn--primary" 
                  style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem', height: '30px' }}
                  onClick={(e) => { e.stopPropagation(); setPublishingPageId(page.id); }}
                >
                  Post
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manage Groups Modal (Inline implementation for simplicity) */}
      {isManageGroupsOpen && (
        <div className="modal-backdrop" onClick={() => setIsManageGroupsOpen(false)}>
          <div className="modal-content glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Управління групами</h3>
            
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
              <input 
                type="text" 
                className="input" 
                placeholder="Нова група (напр. UA)" 
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                style={{ flex: 1 }}
              />
              <button 
                className="btn btn--primary" 
                onClick={() => {
                  if (newGroupName.trim()) {
                    createGroup(newGroupName.trim());
                    setNewGroupName('');
                  }
                }}
              >
                Додати
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '300px', overflowY: 'auto' }}>
              {groupsLoading ? (
                <div className="text-subtle text-sm">Завантаження...</div>
              ) : groups.length === 0 ? (
                <div className="text-subtle text-sm">Груп поки немає</div>
              ) : (
                groups.map(g => (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-2)', background: 'var(--color-surface-1)', borderRadius: 'var(--radius-sm)' }}>
                    <span>{g.name}</span>
                    <button 
                      className="btn btn--danger" 
                      style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      onClick={() => {
                        if (confirm(`Видалити групу ${g.name}?`)) {
                          deleteGroup(g.id);
                        }
                      }}
                    >
                      Видалити
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn--outline" onClick={() => setIsManageGroupsOpen(false)}>
                Закрити
              </button>
            </div>
          </div>
        </div>
      )}

      <PageDetailsModal
        pageId={selectedPageId ?? ''}
        isOpen={!!selectedPageId}
        onClose={() => setSelectedPageId(null)}
        pageDetails={pageDetails}
        pageDetailsLoading={pageDetailsLoading}
        fetchPageDetails={fetchPageDetails}
        updatePageDetails={updatePageDetails}
        syncPageDetails={syncPageDetails}
      />

      {publishingPageId && (
        <PublishPostModal 
          pageId={publishingPageId} 
          pageName={pages.find(p => p.id === publishingPageId)?.name ?? ''}
          onClose={() => setPublishingPageId(null)} 
        />
      )}
    </div>
  );
}
